/* global BigInt */
import {
    AbstractWalletPlugin, WalletPluginMetadata, Signature, Serializer, Transaction, SigningRequest, ResolvedSigningRequest, ChainId,
    PublicKey, KeyType, Bytes, Checksum256
} from "@wharfkit/session";

// WHATIF Wallet: one passkey wallet for every WHATIF site and app, at wallet.rewardsinwhatif.app.
// WAX binds a passkey to the exact host of the page that made it, so this plugin never touches
// passkeys itself: it opens the wallet page, which shows the request and signs with the
// player's passkey (fingerprint, face or PIN).
//   Websites: a popup, answered by postMessage.
//   Phone apps: the phone's browser, answered through the app's login-callback link; the
//   app's appUrlOpen handler must pass that link to handleWhatifWalletReturn() first.
// Google sign-in (googleSession.js) uses it too: setup() has the wallet move a player's account
// to their passkey the first time, and their Google session signs through the wallet.
// Connecting can be the sign-in too: given a one-time challenge from the site's server, the one
// passkey prompt also proves the account to that server (connectProofOf).
// Auto-approve: once the player turns it on in the wallet, this site's game actions are signed
// here, with a key that lives only in this browser (WebCrypto, can't be exported) and that WAX
// accepts only for the actions the player approved. Everything else still goes to the wallet.
// The same file is used by all six WHATIF sites -- keep the copies in sync.

const WALLET_URL = "https://wallet.rewardsinwhatif.app/";
const WALLET_ORIGIN = new URL(WALLET_URL).origin;
const WAX_CHAIN_ID = "1064487b3cd1a897ce03ae5b6a865651747e2e152090f99c1d19d44e01aea5a4";

const toBase64url = (text) => btoa(String.fromCharCode(...new TextEncoder().encode(text)))
    .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
const fromBase64url = (text) => new TextDecoder().decode(Uint8Array.from(atob(text.replace(/-/g, "+").replace(/_/g, "/")), (c) => c.charCodeAt(0)));
const newId = () => Array.from(crypto.getRandomValues(new Uint8Array(12)), (b) => b.toString(16).padStart(2, "0")).join("");

// The app request waiting for its answer (an app sends one wallet request at a time)
let pendingReturn = null;

// --- auto-approve keys, kept in this browser's IndexedDB (CryptoKeys stay non-extractable) ---
const AUTO_DB = "whatif-wallet";
const AUTO_STORE = "autoApprove";
const AUTO_DAYS = 30;
const P256_N = BigInt("0xffffffff00000000ffffffffffffffffbce6faada7179e84f3b9cac2fc632551");
const autoId = (chainId, account) => `${chainId}:${account}`;

function autoStore(mode, use) {
    return new Promise((resolve, reject) => {
        const open = indexedDB.open(AUTO_DB, 1);
        open.onupgradeneeded = () => open.result.createObjectStore(AUTO_STORE);
        open.onerror = () => reject(open.error);
        open.onsuccess = () => {
            const db = open.result;
            const request = use(db.transaction(AUTO_STORE, mode).objectStore(AUTO_STORE));
            request.onsuccess = () => { db.close(); resolve(request.result); };
            request.onerror = () => { db.close(); reject(request.error); };
        };
    });
}
const autoGet = (id) => autoStore("readonly", (store) => store.get(id));
const autoPut = (id, value) => autoStore("readwrite", (store) => store.put(value, id));
const autoDelete = (id) => autoStore("readwrite", (store) => store.delete(id)).catch(() => {});

// A P-256 key the browser won't export, as a WAX "R1" key
async function newAutoKey() {
    const pair = await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign"]);
    const raw = new Uint8Array(await crypto.subtle.exportKey("raw", pair.publicKey));
    const compressed = Uint8Array.from([raw[64] & 1 ? 3 : 2, ...raw.slice(1, 33)]);
    return { privateKey: pair.privateKey, publicKey: String(new PublicKey(KeyType.R1, Bytes.from(compressed))) };
}

const toHex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
const bytes32 = (n) => Uint8Array.from(n.toString(16).padStart(64, "0").match(/../g), (h) => parseInt(h, 16));
// WAX's rule for these signatures, as for its ordinary keys: r and s below 2^255, not padded
const canonical = (r, s) => !(r[0] & 0x80) && !(r[0] === 0 && !(r[1] & 0x80)) && !(s[0] & 0x80) && !(s[0] === 0 && !(s[1] & 0x80));

// WAX signature over `tx` with the auto-approve key. WebCrypto hashes what it signs, so it signs
// the chain's signing data (chain id, transaction, empty context-free hash); the signature is
// retried until it's canonical, with the low S and the recovery id WAX expects.
async function autoSign(entry, chainId, tx) {
    const encoded = Serializer.encode({ object: tx }).array;
    const data = new Uint8Array(32 + encoded.length + 32);
    data.set(Checksum256.from(chainId).array, 0);
    data.set(encoded, 32);
    const digest = tx.signingDigest(chainId);
    const key = PublicKey.from(entry.publicKey);
    for (let attempt = 0; attempt < 64; attempt++) {
        const raw = new Uint8Array(await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, entry.privateKey, data));
        const r = raw.slice(0, 32);
        let s = BigInt(`0x${toHex(raw.slice(32))}`);
        if (s > P256_N / BigInt(2)) s = P256_N - s;
        const sBytes = bytes32(s);
        if (!canonical(r, sBytes)) continue;
        for (let recid = 0; recid < 4; recid++) {
            try {
                const signature = new Signature(KeyType.R1, Bytes.from(Uint8Array.from([31 + recid, ...r, ...sBytes])));
                if (signature.recoverDigest(digest).equals(key)) return signature;
            } catch (e) {
                // not this recovery id
            }
        }
    }
    throw new Error("Couldn't sign with this device's auto-approve key.");
}

// Every action is one the player auto-approved, for this account's active permission alone
function autoEligible(tx, account, actions) {
    return tx.actions.length > 0 && !tx.context_free_actions.length && tx.actions.every((a) =>
        actions.includes(`${a.account}::${a.name}`) && a.authorization.length === 1
        && String(a.authorization[0].actor) === account && String(a.authorization[0].permission) === "active");
}

// The same transaction, authorized by the site's auto-approve permission instead of active
function withPermission(tx, account, permission) {
    const plain = Serializer.objectify(tx);
    plain.actions = plain.actions.map((a) => ({ ...a, authorization: [{ actor: account, permission }] }));
    return Transaction.from(plain);
}

// A plain "Continue" box for sessions without a WharfKit prompt (Google sign-in): its click is
// what lets the browser open the wallet popup.
function askToContinue(onContinue, onCancel) {
    const overlay = document.createElement("div");
    overlay.setAttribute("style", "position:fixed;inset:0;z-index:2147483647;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(0,0,0,.6)");
    const box = document.createElement("div");
    box.setAttribute("style", "width:100%;max-width:340px;padding:20px;border-radius:14px;background:#12121c;color:#e8e8f0;font:15px/1.5 system-ui,sans-serif;text-align:center");
    const text = document.createElement("p");
    text.setAttribute("style", "margin:0 0 16px");
    text.textContent = "Continue in WHATIF Wallet to approve with your passkey.";
    const button = (label, primary, onClick) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.setAttribute("style", `display:block;width:100%;margin-top:8px;padding:12px;border:0;border-radius:10px;font-weight:700;cursor:pointer;${primary ? "background:#4dff4d;color:#000" : "background:#2c2c40;color:#e8e8f0"}`);
        b.addEventListener("click", () => { overlay.remove(); onClick(); });
        return b;
    };
    box.append(text, button("Continue", true, onContinue), button("Cancel", false, onCancel));
    overlay.append(box);
    document.body.append(overlay);
}

/**
 * For phone apps: call first in the app's appUrlOpen handler. Returns true if the link was the
 * wallet's answer (and handles it), false for any other link.
 */
export function handleWhatifWalletReturn(url) {
    const match = /[?&]whatifWallet=([A-Za-z0-9_-]+)/.exec(String(url || ""));
    if (!match) return false;
    let answer = null;
    try {
        answer = JSON.parse(fromBase64url(match[1]));
    } catch (e) {
        // a broken link: nothing to answer
    }
    if (answer && pendingReturn && answer.id === pendingReturn.id) {
        const pending = pendingReturn;
        pendingReturn = null;
        if (answer.error) pending.reject(new Error(answer.error));
        else pending.resolve(answer.result);
    }
    return true;
}

/**
 * What a wallet proved as it connected, for the site's server to check, from a sessionKit.login()
 * result: WHATIF Wallet's passkey and Anchor both sign as they connect, so the player has nothing
 * more to approve. Null for wallets that don't.
 */
export function connectProofOf(loginResult) {
    const { loginProof, identityProof } = loginResult?.response || {};
    if (loginProof) return { type: "passkey", ...loginProof };
    if (identityProof) {
        return {
            type: "anchor",
            scope: String(identityProof.scope),
            permission: String(identityProof.signer.permission),
            expiration: String(identityProof.expiration),
            signature: String(identityProof.signature)
        };
    }
    return null;
}

// The site's challenge, or null if it failed or is slow: the player still connects, and signs in
// the longer way after.
const challengeFrom = (fetchNonce) => Promise.race([
    Promise.resolve().then(fetchNonce).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), 8000))
]);

export class WhatifWalletPlugin extends AbstractWalletPlugin {
    /**
     * @param {object} [options]
     * @param {string} [options.returnUrl] Phone apps only: the app's login-callback link.
     * @param {function} [options.openUrl] Phone apps only: opens a URL in the phone's browser.
     * @param {function} [options.loginNonce] Sites whose server signs players in: resolves with a
     *   one-time challenge from that server.
     */
    constructor(options = {}) {
        super();
        this.id = "whatif-wallet";
        this.config = { requiresChainSelect: false, requiresPermissionSelect: false, supportedChains: [WAX_CHAIN_ID] };
        this.metadata = new WalletPluginMetadata({
            name: "WHATIF Wallet",
            description: "One passkey wallet for every WHATIF site and app",
            homepage: WALLET_URL
        });
        this.returnUrl = options.returnUrl;
        this.openUrl = options.openUrl || ((url) => window.open(url, "_blank"));
        this.loginNonce = options.loginNonce;
    }

    async login(context) {
        const result = await this.request(context, {
            type: "connect",
            chainId: String(context.chain.id),
            loginNonce: this.loginNonce ? challengeFrom(this.loginNonce) : null
        });
        this.data.credentialId = result.credentialId;
        return {
            chain: context.chain.id,
            permissionLevel: { actor: result.account, permission: result.permission || "active" },
            loginProof: result.loginProof
        };
    }

    async sign(resolved, context) {
        const account = String(context.permissionLevel.actor);
        const chainId = String(context.chain.id);
        const auto = await this.autoKey(chainId, account, context);
        if (auto && autoEligible(resolved.transaction, account, auto.actions)) {
            const tx = withPermission(resolved.transaction, account, auto.permission);
            const signature = await autoSign(auto, chainId, tx);
            const request = await SigningRequest.create({ transaction: tx }, context.esrOptions);
            return {
                resolved: new ResolvedSigningRequest(request, context.permissionLevel, tx, Serializer.objectify(tx), ChainId.from(chainId)),
                signatures: [signature]
            };
        }
        const offer = await this.autoOffer(chainId, account);
        const result = await this.request(context, {
            type: "sign",
            chainId,
            account,
            credentialId: this.data.credentialId,
            transaction: Serializer.encode({ object: resolved.transaction }).hexString,
            ...(offer ? { autoApprove: { publicKey: offer.publicKey } } : {})
        });
        if (offer && result.autoApprove) {
            const { permission, actions } = result.autoApprove;
            await autoPut(autoId(chainId, account), { ...offer, state: "on", permission, actions, since: Date.now() }).catch(() => {});
        }
        return { signatures: result.signatures.map((s) => Signature.from(s)) };
    }

    // This browser's auto-approve key for `account`, if it's on and WAX still has it (the player
    // can turn it off in the wallet at any time).
    async autoKey(chainId, account, context) {
        let entry;
        try {
            entry = await autoGet(autoId(chainId, account));
        } catch (e) {
            return null;
        }
        if (!entry || entry.state !== "on") return null;
        if (Date.now() - entry.since > AUTO_DAYS * 86400000) {
            await autoDelete(autoId(chainId, account));
            return null;
        }
        try {
            const data = await context.client.v1.chain.get_account(account);
            const permission = data.permissions.find((p) => String(p.perm_name) === entry.permission);
            if (permission && permission.required_auth.keys.some((k) => String(k.key) === entry.publicKey)) return entry;
        } catch (e) {
            return null; // can't check right now: the wallet signs instead
        }
        await autoDelete(autoId(chainId, account));
        return null;
    }

    // A key this browser can offer the wallet for auto-approve: made once, kept until it's on.
    async autoOffer(chainId, account) {
        try {
            if (typeof indexedDB === "undefined" || !crypto.subtle) return null;
            const id = autoId(chainId, account);
            const entry = await autoGet(id);
            if (entry) return entry.state === "offered" ? entry : null;
            const offer = { ...(await newAutoKey()), state: "offered" };
            await autoPut(id, offer);
            return offer;
        } catch (e) {
            return null;
        }
    }

    /**
     * First Google sign-in (googleSession.js): the wallet has the player make their passkey and
     * backup code, and moves their account to them (or creates it). `ticket` is the one-time
     * setup ticket from puzzl_authGoogleWaxAccount. Resolves with { account }.
     */
    setup(ticket) {
        return this.request({}, { type: "setup", chainId: WAX_CHAIN_ID, ticket });
    }

    request(context, request) {
        const withId = { ...request, id: newId() };
        return this.returnUrl ? this.viaApp(withId) : this.viaPopup(context, withId);
    }

    async viaApp(request) {
        const loginNonce = await request.loginNonce;
        return new Promise((resolve, reject) => {
            if (pendingReturn) pendingReturn.reject(new Error("Replaced by a newer WHATIF Wallet request."));
            pendingReturn = { id: request.id, resolve, reject };
            this.openUrl(`${WALLET_URL}#req=${toBase64url(JSON.stringify({ ...request, loginNonce, returnUrl: this.returnUrl }))}`);
        });
    }

    async viaPopup(context, request) {
        // A new window each time: reusing a named one reloads it wherever it is, which on a computer
        // can be hidden behind the site, so it looks like nothing happened
        const open = () => window.open(`${WALLET_URL}?popup=1`, "_blank", "width=460,height=720");
        let popup = open();
        if (!popup) {
            // Browsers only open popups straight after a click: ask for one
            popup = await new Promise((resolve, reject) => {
                if (!context.ui) return askToContinue(() => resolve(open()), () => reject(new Error("Cancelled.")));
                const prompt = context.ui.prompt({
                    title: "WHATIF Wallet",
                    body: "Continue in WHATIF Wallet to approve with your passkey.",
                    elements: [{
                        type: "button",
                        data: { label: "Continue", variant: "primary", onClick: () => { prompt.cancel(); resolve(open()); } }
                    }]
                });
                prompt.catch(() => reject(new Error("Cancelled.")));
            });
            if (!popup) throw new Error("Allow pop-ups for this site to use WHATIF Wallet.");
        }
        // The window has to open straight away, but the wallet says hello until it's answered, so
        // the request can wait for the site's challenge
        const ready = { ...request, loginNonce: await request.loginNonce };
        return new Promise((resolve, reject) => {
            const finish = () => {
                window.removeEventListener("message", onMessage);
                clearInterval(closedCheck);
            };
            const onMessage = (event) => {
                if (event.origin !== WALLET_ORIGIN || event.source !== popup) return;
                const msg = event.data || {};
                if (msg.type === "whatif-wallet:ready") {
                    popup.postMessage({ type: "whatif-wallet:request", request: ready }, WALLET_ORIGIN);
                } else if (msg.type === "whatif-wallet:response" && msg.id === request.id) {
                    finish();
                    if (msg.error) reject(new Error(msg.error));
                    else resolve(msg.result);
                }
            };
            const closedCheck = setInterval(() => {
                if (popup.closed) {
                    finish();
                    reject(new Error("WHATIF Wallet was closed."));
                }
            }, 500);
            window.addEventListener("message", onMessage);
        });
    }
}
